import * as z from 'zod/mini';
import { createLocalStorageState } from './manager';
import { scheduleIdSchema, scheduleTypeSchema } from '../../api/common';
import { scheduleIdsGlobalState } from '../queryParams/scheduleIds';
import { scheduleTypeGlobalState } from '../queryParams/scheduleType';
import { hiddenSubjectsGlobalState } from '../queryParams/hiddenSubjects';

const savedSchedulesSchemaForParse = z.array(
    z.object({
        type: scheduleTypeSchema,
        headers: z.array(
            z.object({
                id: scheduleIdSchema,
                name: z.string().check(z.minLength(1)),
            }),
        ),
        hiddenSubjects: z.pipe(
            z.union([
                z.pipe(
                    z.array(z.string()),
                    z.transform(() => [] as [string, string[]][]),
                ),
                z.array(z.tuple([z.string(), z.array(z.string())])),
            ]),
            z.transform(
                (entries) =>
                    Object.fromEntries(
                        entries.map((entry) => [
                            entry[0],
                            Array.from(new Set(entry[1])).sort((a, b) => a.localeCompare(b)),
                        ]),
                    ) as Record<string, string[]>,
            ),
        ),
    }),
);

export type SavedSchedule = z.infer<typeof savedSchedulesSchemaForParse>[number];

const baseState = createLocalStorageState(
    'savedSchedules',
    (encodedValue) => {
        try {
            return savedSchedulesSchemaForParse.parse(JSON.parse(encodedValue));
        } catch (_) {
            return [];
        }
    },
    (values) =>
        JSON.stringify(
            values.map((value) => ({
                ...value,
                hiddenSubjects: Object.entries(value.hiddenSubjects).map((entry) => [
                    entry[0],
                    Array.from(new Set(entry[1])).sort((a, b) => a.localeCompare(b)),
                ]),
            })),
        ),
);

export const savedSchedulesGlobalState = {
    ...baseState,
    add: (newSavedSchedule: SavedSchedule) => baseState.set([...baseState.get(), newSavedSchedule]),
    remove: (savedScheduleToDelete: SavedSchedule) =>
        baseState.set(baseState.get().filter((savedSchedule) => savedSchedule !== savedScheduleToDelete)),
    moveBack: (savedScheduleToMove: SavedSchedule) => {
        const newSavedSchedules = [...baseState.get()];

        const savedScheduleToMoveIdx = newSavedSchedules.findIndex(
            (savedSchedule) => savedSchedule === savedScheduleToMove,
        );
        if (savedScheduleToMoveIdx > 0) {
            const tmp = newSavedSchedules[savedScheduleToMoveIdx - 1]!;
            newSavedSchedules[savedScheduleToMoveIdx - 1] = newSavedSchedules[savedScheduleToMoveIdx]!;
            newSavedSchedules[savedScheduleToMoveIdx] = tmp;
        }

        baseState.set(newSavedSchedules);
    },
    moveForward: (savedScheduleToMove: SavedSchedule) => {
        const newSavedSchedules = [...baseState.get()];

        const savedScheduleToMoveIdx = newSavedSchedules.findIndex(
            (savedSchedule) => savedSchedule === savedScheduleToMove,
        );
        if (savedScheduleToMoveIdx !== -1 && savedScheduleToMoveIdx < newSavedSchedules.length - 1) {
            const tmp = newSavedSchedules[savedScheduleToMoveIdx + 1]!;
            newSavedSchedules[savedScheduleToMoveIdx + 1] = newSavedSchedules[savedScheduleToMoveIdx]!;
            newSavedSchedules[savedScheduleToMoveIdx] = tmp;
        }

        baseState.set(newSavedSchedules);
    },
};

export const createSavedScheduleSelectionUpdates = (savedSchedule: SavedSchedule) =>
    [
        scheduleIdsGlobalState.createUpdate(savedSchedule.headers.map((header) => header.id)),
        scheduleTypeGlobalState.createUpdate(savedSchedule.type),
        hiddenSubjectsGlobalState.createUpdate(savedSchedule.hiddenSubjects),
    ] as const;
